import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Button, Modal, Form, Input, Select, Rate, Tag, Row, Col, Statistic, message, Space, Avatar, Tooltip, DatePicker, Empty, Progress } from 'antd';
import { SmileOutlined, TrophyOutlined, HeartOutlined, PlusOutlined, SendOutlined, StarOutlined } from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import dayjs from 'dayjs';
import apiService from 'services/api/apiService';

const { TextArea } = Input;

const moodEmojis: Record<number, string> = { 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '😄' };
const moodColors: Record<number, string> = { 1: '#ff4d4f', 2: '#fa8c16', 3: '#faad14', 4: '#52c41a', 5: '#13c2c2' };
const awardColors: Record<string, string> = { spot_award: 'gold', peer_recognition: 'blue', milestone: 'purple', excellence: 'red', innovation: 'cyan', teamwork: 'green' };

const EngagementDashboard: React.FC = () => {
  const [surveys, setSurveys] = useState<any[]>([]);
  const [recognitions, setRecognitions] = useState<any[]>([]);
  const [moodData, setMoodData] = useState<any[]>([]);
  const [moodStats, setMoodStats] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [surveyModal, setSurveyModal] = useState(false);
  const [recognitionModal, setRecognitionModal] = useState(false);
  const [moodModal, setMoodModal] = useState(false);
  const [surveyForm] = Form.useForm();
  const [recognitionForm] = Form.useForm();
  const [moodForm] = Form.useForm();
  const [period, setPeriod] = useState(dayjs().format('YYYY-MM'));

  useEffect(() => { fetchAll(); }, [period]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sr, rr, mr, smr, er] = await Promise.all([
        apiService.getEngagementSurveys(),
        apiService.getRecognitions(),
        apiService.getMoodCheckins(period),
        apiService.getEngagementSummary(),
        apiService.getEmployees(),
      ]);
      if (sr.success) setSurveys(sr.data || []);
      if (rr.success) setRecognitions(rr.data || []);
      if (mr.success) { setMoodData(mr.data || []); setMoodStats(mr.stats); }
      if (smr.success) setSummary(smr.data);
      if (er.success) setEmployees(er.data || []);
    } finally { setLoading(false); }
  };

  const handleCreateSurvey = async (values: any) => {
    const questions = (values.questions_text || '').split('\n').filter((q: string) => q.trim()).map((q: string, i: number) => ({ id: i+1, text: q.trim(), type: 'rating' }));
    const r = await apiService.createSurvey({ ...values, questions });
    if (r.success) { message.success('Survey created'); setSurveyModal(false); surveyForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleRecognition = async (values: any) => {
    const r = await apiService.sendRecognition(values);
    if (r.success) { message.success('Recognition sent! 🎉'); setRecognitionModal(false); recognitionForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const handleMoodCheckin = async (values: any) => {
    let empId = null;
    try { const tok = localStorage.getItem('hr_access_token') || ''; empId = JSON.parse(atob(tok.split('.')[1] || tok)).id || null; } catch(e) {}
    const r = await apiService.submitMoodCheckin({ ...values, employee_id: empId, checkin_date: dayjs().format('YYYY-MM-DD') });
    if (r.success) { message.success('Mood recorded'); setMoodModal(false); moodForm.resetFields(); fetchAll(); }
    else message.error(r.error || 'Failed');
  };

  const moodTrendData = moodData.slice(0, 30).reverse().map(m => ({ date: m.checkin_date, score: m.mood_score, name: m.employee_name }));

  const tabItems = [
    {
      key: 'overview',
      label: '📊 Overview',
      children: (
        <div>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}><Card><Statistic title="Active Surveys" value={summary?.active_surveys || 0} prefix={<SmileOutlined />} /></Card></Col>
            <Col span={6}><Card><Statistic title="Total Responses" value={summary?.total_responses || 0} /></Card></Col>
            <Col span={6}><Card><Statistic title="Recognitions Sent" value={recognitions.length} prefix={<TrophyOutlined />} /></Card></Col>
            <Col span={6}><Card><Statistic title="Avg Mood Score" value={moodStats?.avg_mood || 0} suffix="/5" precision={1} prefix={<HeartOutlined />} /></Card></Col>
          </Row>
          {moodTrendData.length > 0 && (
            <Card title="Team Mood Trend" style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={moodTrendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[1,5]} />
                  <ChartTooltip />
                  <Line type="monotone" dataKey="score" stroke="#1890ff" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}
          <Card title="Recent Recognitions">
            {recognitions.length === 0 ? <Empty description="No recognitions yet" /> : (
              recognitions.slice(0, 5).map((r: any) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Avatar style={{ background: '#1890ff' }}>{r.from_name?.charAt(0)}</Avatar>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{r.from_name}</span> recognised <span style={{ fontWeight: 600 }}>{r.to_name}</span>
                    <Tag color={awardColors[r.award_type] || 'blue'} style={{ marginLeft: 8 }}>{r.award_type?.replace('_',' ')}</Tag>
                    <div style={{ fontSize: 13, color: '#666' }}>{r.message}</div>
                  </div>
                  <Tag color="gold">+{r.points} pts</Tag>
                </div>
              ))
            )}
          </Card>
        </div>
      )
    },
    {
      key: 'surveys',
      label: '📋 Pulse Surveys',
      children: (
        <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setSurveyModal(true)}>Create Survey</Button>}>
          <Table dataSource={surveys} rowKey="id" pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Title', dataIndex: 'title', key: 'title' },
              { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag color={s==='active'?'success':s==='closed'?'default':'warning'}>{s?.toUpperCase()}</Tag> },
              { title: 'Responses', dataIndex: 'response_count', render: (v: number) => v || 0 },
              { title: 'Start Date', dataIndex: 'start_date' },
              { title: 'End Date', dataIndex: 'end_date' },
            ]} />
        </Card>
      )
    },
    {
      key: 'recognition',
      label: '🏆 Recognition',
      children: (
        <Card extra={<Button type="primary" icon={<SendOutlined />} onClick={() => setRecognitionModal(true)}>Send Recognition</Button>}>
          <Table dataSource={recognitions} rowKey="id" pagination={{ pageSize: 10 }}
            columns={[
              { title: 'From', dataIndex: 'from_name' },
              { title: 'To', dataIndex: 'to_name' },
              { title: 'Award', dataIndex: 'award_type', render: (v: string) => <Tag color={awardColors[v]||'blue'}>{v?.replace('_',' ')}</Tag> },
              { title: 'Title', dataIndex: 'title' },
              { title: 'Points', dataIndex: 'points', render: (v: number) => <Tag color="gold">+{v}</Tag> },
              { title: 'Date', dataIndex: 'created_at', render: (v: string) => dayjs(v).format('DD MMM YYYY') },
            ]} />
        </Card>
      )
    },
    {
      key: 'mood',
      label: '😊 Mood Tracking',
      children: (
        <div>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}><DatePicker picker="month" value={dayjs(period)} onChange={d => setPeriod(d ? d.format('YYYY-MM') : dayjs().format('YYYY-MM'))} /></Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<SmileOutlined />} onClick={() => setMoodModal(true)}>Check In My Mood</Button>
            </Col>
          </Row>
          {moodStats && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}><Card><Statistic title="Avg Mood" value={moodStats.avg_mood || 0} suffix="/5" precision={1} /></Card></Col>
              <Col span={6}><Card><Statistic title="Total Check-ins" value={moodStats.total_checkins || 0} /></Card></Col>
              <Col span={6}><Card><Statistic title="Positive" value={moodStats.positive_count || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
              <Col span={6}><Card><Statistic title="At Risk" value={moodStats.negative_count || 0} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
            </Row>
          )}
          <Card title="Mood Check-ins">
            <Table dataSource={moodData} rowKey="id" pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Employee', dataIndex: 'employee_name' },
                { title: 'Department', dataIndex: 'department' },
                { title: 'Mood', dataIndex: 'mood_score', render: (v: number) => <span style={{ fontSize: 20 }}>{moodEmojis[v]} <Tag color={moodColors[v]}>{v}/5</Tag></span> },
                { title: 'Notes', dataIndex: 'notes', render: (v: string) => v || '-' },
                { title: 'Date', dataIndex: 'checkin_date' },
              ]} />
          </Card>
        </div>
      )
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Employee Engagement</h1>
      <Tabs items={tabItems} />

      {/* Create Survey Modal */}
      <Modal title="Create Pulse Survey" open={surveyModal} onCancel={() => { setSurveyModal(false); surveyForm.resetFields(); }} footer={null} width={600}>
        <Form form={surveyForm} layout="vertical" onFinish={handleCreateSurvey}>
          <Form.Item name="title" label="Survey Title" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><TextArea rows={2} /></Form.Item>
          <Form.Item name="questions_text" label="Questions (one per line)" rules={[{ required: true }]}><TextArea rows={5} placeholder="How satisfied are you with your work environment?&#10;Do you feel valued by your manager?&#10;Would you recommend this company to a friend?" /></Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="start_date" label="Start Date"><Input type="date" /></Form.Item></Col>
            <Col span={12}><Form.Item name="end_date" label="End Date"><Input type="date" /></Form.Item></Col>
          </Row>
          <Form.Item name="status" label="Status" initialValue="draft">
            <Select><Select.Option value="draft">Draft</Select.Option><Select.Option value="active">Active</Select.Option></Select>
          </Form.Item>
          <Space><Button type="primary" htmlType="submit">Create Survey</Button><Button onClick={() => setSurveyModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Recognition Modal */}
      <Modal title="🏆 Send Recognition" open={recognitionModal} onCancel={() => { setRecognitionModal(false); recognitionForm.resetFields(); }} footer={null} width={500}>
        <Form form={recognitionForm} layout="vertical" onFinish={handleRecognition}>
          <Form.Item name="to_employee_id" label="Recognise" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Select employee">
              {employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="from_employee_id" label="From (your employee ID)" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Select yourself">
              {employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="award_type" label="Award Type" initialValue="peer_recognition">
            <Select>{Object.keys(awardColors).map(k => <Select.Option key={k} value={k}><Tag color={awardColors[k]}>{k.replace('_',' ')}</Tag></Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}><Input placeholder="e.g. Outstanding Teamwork on Q2 Project" /></Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}><TextArea rows={3} placeholder="Share what they did that deserves recognition..." /></Form.Item>
          <Form.Item name="points" label="Points" initialValue={10}><Input type="number" min={1} max={100} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Send Recognition 🎉</Button><Button onClick={() => setRecognitionModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>

      {/* Mood Check-in Modal */}
      <Modal title="😊 Daily Mood Check-in" open={moodModal} onCancel={() => { setMoodModal(false); moodForm.resetFields(); }} footer={null} width={400}>
        <Form form={moodForm} layout="vertical" onFinish={handleMoodCheckin}>
          <Form.Item name="mood_score" label="How are you feeling today?" rules={[{ required: true }]}>
            <Select size="large">
              {[1,2,3,4,5].map(s => <Select.Option key={s} value={s}><span style={{ fontSize: 20 }}>{moodEmojis[s]}</span> {['','Terrible','Bad','Neutral','Good','Excellent'][s]}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Anything you'd like to share? (optional)"><TextArea rows={3} placeholder="Optional — this is anonymous..." /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Submit Check-in</Button><Button onClick={() => setMoodModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};

export default EngagementDashboard;
